import * as THREE from "three";
import { Enemy, LevelSim, SimEvent, SimEventKind } from "@highmarch/domain";
import { groundAt } from "./terrain";
import { PET_ROUND, roundOf } from "./statusFx";
import { ProjectileViews, UNIT_SHOT } from "./views/projectiles";

/** What the VFX router (vfxRouter.ts) reads off a tick's events and the sim: who was hit, where rounds land, how long they fly. */

/**
 * The enemy an event of this tick names, alive or killed during the tick. `LevelSim.findEnemy` returns living enemies
 * only, but the sim emits `TowerFired` before the hit that kills and `BombDropped` after the bomber has died or leaked,
 * so a lookup through it silently dropped every killing shot and every bomb. A dead slot keeps its last position
 * until a spawn reuses it, and a reused slot carries a new id, so matching the id is enough.
 */
export function enemyThisTick(sim: LevelSim, id: number): Enemy | null {
  for (const e of sim.enemySlots) if (e.id === id) return e;
  return null;
}

/** Where the enemy views draw an enemy's feet this frame (EnemyViews.feet), or null when it has no view. */
export type DrawnFeet = (enemyId: number) => THREE.Vector3 | null;
/** Where weapons aim at a flyer this frame (EnemyViews.airAim: its hovering body), or null for a ground unit. */
export type AirAim = (enemyId: number) => THREE.Vector3 | null;

/** The point to aim at enemy `e` this tick: a flyer's drawn body (`air`), else `lift` over the ground under it. */
export function aimAt(e: Enemy, lift: number, air: AirAim | null): THREE.Vector3 {
  const a = air?.(e.id);
  return a ? a.clone() : new THREE.Vector3(e.x.toNumber(), groundAt(e.x.toNumber(), e.y.toNumber()) + lift, e.y.toNumber());
}

/**
 * A projectile's target: the enemy as drawn (`drawn`), which is the body still walking on after the sim killed it
 * (views/enemyGhost.ts), so the round lands on it; without a view, the sim's enemy while it lives, then the spot it
 * was last seen, so a round still lands on a kill.
 */
export function follow(sim: LevelSim, id: number, lift: number, seen: THREE.Vector3, drawn: DrawnFeet | null = null, air: AirAim | null = null): () => THREE.Vector3 {
  const last = seen.clone();
  return () => {
    const a = air?.(id);
    if (a) return last.copy(a);                         // a flyer: its hovering body, not the ground under it
    const d = drawn?.(id);
    if (d) { last.set(d.x, groundAt(d.x, d.z) + lift, d.z); return last; }
    const x = sim.findEnemy(id);
    if (x) last.set(x.x.toNumber(), groundAt(x.x.toNumber(), x.y.toNumber()) + lift, x.y.toNumber());
    return last;
  };
}

/** The fortress strike's bolt lands 100 ms after the event (vfx.js baseStrike leader); its hits wait at most this long. */
export const STRIKE_LANDS_WITHIN = 0.4;

/** A tower round aims this far over the ground under a ground unit (a flyer: its body, AirAim). */
export const ROUND_LIFT = 0.12;

/** A piercing bolt passes through its victims this far over the ground (the height every tower round aims at). */
export const PIERCE_LIFT = 0.12;

/** A kill's effects stand this far over the ground under the body. */
export const DEATH_LIFT = 0.05;

/** A wasted bomb's short, low lob: this many cells from where the bomber fell, at this arc height. */
export const WASTED_BOMB_HOP = 0.6;
const WASTED_BOMB_ARC = 0.45;
export const WASTED_BOMB = { ...UNIT_SHOT.bomber, arc: WASTED_BOMB_ARC };

/**
 * Where a wasted bomb (BombDropped naming no tower) lands: `WASTED_BOMB_HOP` cells from the bomber's spot toward the
 * nearest of `towers` (x = cell x, z = cell y), or on the spot itself when there are none or one stands right there.
 */
export function wastedBombLanding(x: number, z: number, towers: readonly { x: number; y: number }[]): { x: number; z: number } {
  let best: { x: number; y: number } | null = null, bestD2 = Infinity;
  for (const c of towers) { const d2 = (c.x - x) ** 2 + (c.y - z) ** 2; if (d2 < bestD2) { best = c; bestD2 = d2; } }
  if (!best || bestD2 < 1e-6) return { x, z };
  const d = Math.sqrt(bestD2), k = Math.min(WASTED_BOMB_HOP, d) / d;
  return { x: x + (best.x - x) * k, z: z + (best.y - z) * k };
}

/**
 * Seconds a round flies from `from` to the farthest of `to` at `speed` (views/projectiles.ts: every round's flight
 * lasts its chord over its speed, 0.08 s at least), plus a pierce's overshoot past its last victim.
 */
export function roundFlight(from: THREE.Vector3, to: readonly THREE.Vector3[], speed: number, pierce = false): number {
  let far = 0;
  for (const p of to) far = Math.max(far, from.distanceTo(p));
  if (pierce) far += ProjectileViews.PierceOvershoot;
  return Math.max(ProjectileViews.MinFlightSeconds, far / Math.max(1e-3, speed));
}

/**
 * Whether `ev` came with its source tower's round of this tick, i.e. that tower fired before it in the buffer (the
 * rule of statusFx.ts roundOf, which the enemy view and the sounds follow): a burn tick that kills comes earlier.
 */
export function byRound(sim: LevelSim, ev: SimEvent): boolean {
  if (ev.b <= 0) return false;
  for (const x of sim.events.all()) {
    if (x === ev) return false;
    if (x.kind === SimEventKind.TowerFired && x.a === ev.b) return true;
  }
  return false;
}

/** Whether `ev` (a kill) is the pet's blow's (statusFx.ts roundOf: PET_ROUND), shown when the pet's blow lands. */
export function byPet(sim: LevelSim, ev: SimEvent): boolean {
  if (ev.b > 0) return false;
  const all = sim.events.all(), i = all.indexOf(ev);
  return i >= 0 && roundOf(all)[i] === PET_ROUND;
}

/**
 * Where the enemies a piercing tower hit this tick stand (EnemyDamaged a = enemy, b = tower): the ballista's bolt
 * flies through each of them, as the sim's pierce did (combat.ts firePierce).
 */
export function pierced(sim: LevelSim, towerId: number, air: AirAim | null = null): { at: THREE.Vector3[]; ids: number[] } {
  const out = { at: [] as THREE.Vector3[], ids: [] as number[] };
  for (const ev of sim.events.all()) {
    if (ev.kind !== SimEventKind.EnemyDamaged || ev.b !== towerId || out.ids.includes(ev.a)) continue;
    const e = enemyThisTick(sim, ev.a);
    if (e) { out.at.push(aimAt(e, PIERCE_LIFT, air)); out.ids.push(e.id); }
  }
  return out;
}

/** Tesla arcs jump on to this tower's chain hits from the same tick (`ChainHit` a = enemy, b = tower, c = hop). */
export function chainTargets(sim: LevelSim, towerId: number, firstId: number, first: THREE.Vector3, air: AirAim | null = null): { at: THREE.Vector3[]; ids: number[] } {
  const out = { at: [first], ids: [firstId] };
  for (const ev of sim.events.all()) {
    if (ev.kind !== SimEventKind.ChainHit || ev.b !== towerId || ev.a === firstId) continue;
    const e = enemyThisTick(sim, ev.a);
    if (e) { out.at.push(aimAt(e, ROUND_LIFT, air)); out.ids.push(e.id); }
    if (out.at.length >= 4) break;
  }
  return out;
}

/** The ground under a boss view's root: where its arrival and fall burst. */
export function feetOf(root: THREE.Object3D): THREE.Vector3 {
  const p = root.getWorldPosition(new THREE.Vector3());
  p.y = groundAt(p.x, p.z) + 0.05;
  return p;
}
